import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/services/apiClient";
import { useTenantStore } from "@/stores/useTenantStore";

/**
 * Structured payload from sandbox output() bridge.
 * Each action type has its own required fields.
 */
interface SchedulePayload {
	action: "schedule" | "reschedule";
	contentId: string;
	time: string; // ISO 8601
}

interface CancelSchedulePayload {
	action: "cancel_schedule";
	contentId: string;
}

interface NavigatePayload {
	action: "navigate";
	path: string;
}

interface CopyPayload {
	action: "copy";
	text: string;
}

interface RefreshPayload {
	action: "refresh";
	queryKey?: string;
}

type SandboxOutputPayload =
	| SchedulePayload
	| CancelSchedulePayload
	| NavigatePayload
	| CopyPayload
	| RefreshPayload;

function isValidPayload(payload: unknown): payload is SandboxOutputPayload {
	if (!payload || typeof payload !== "object") return false;
	const p = payload as Record<string, unknown>;
	return typeof p.action === "string";
}

/**
 * Hook that returns an output handler for the Arrow JS sandbox bridge.
 * Maps sandbox output() payloads to React actions (API calls, navigation, clipboard, etc.)
 *
 * @param onChatFollowUp - Optional callback to inject a follow-up message into the chat
 */
export function useSandboxActions(
	onChatFollowUp?: (message: string) => void,
) {
	console.log("[useSandboxActions] Hook initialized", { hasOnChatFollowUp: !!onChatFollowUp });
	const navigate = useNavigate();
	const currentTenant = useTenantStore((state) => state.currentTenant);

	const handleOutput = useCallback(
		async (payload: unknown) => {
			if (!isValidPayload(payload)) {
				console.warn("[SandboxActions] Invalid output payload:", payload);
				return;
			}

			console.log("[SandboxActions] Received action:", payload.action, payload);

			try {
				switch (payload.action) {
					case "schedule":
					case "reschedule": {
						console.log(`[SandboxActions] Executing ${payload.action} action`, { contentId: payload.contentId, time: payload.time });
						if (!currentTenant) {
							toast.error("Select a workspace before scheduling content.");
							return;
						}
						const res = await apiClient.patch(
							`/blog-creation/content/${payload.contentId}/schedule?tenant_id=${currentTenant}`,
							{ scheduled_time: payload.time },
						);
						const date = new Date(payload.time);
						const formatted = date.toLocaleString(undefined, {
							dateStyle: "medium",
							timeStyle: "short",
						});
						toast.success(`Content scheduled for ${formatted}`);
						onChatFollowUp?.(
							`Content "${res.data.title || payload.contentId}" scheduled for ${formatted}.`,
						);
						break;
					}

					case "cancel_schedule": {
						console.log("[SandboxActions] Executing cancel_schedule action", { contentId: payload.contentId });
						if (!currentTenant) {
							toast.error("Select a workspace before cancelling a schedule.");
							return;
						}
						const res = await apiClient.delete(
							`/blog-creation/content/${payload.contentId}/schedule?tenant_id=${currentTenant}`,
						);
						console.log("[SandboxActions] Schedule cancelled successfully", { title: res.data.title });
						toast.success("Schedule cancelled");
						onChatFollowUp?.(
							`Schedule cancelled for "${res.data.title || payload.contentId}".`,
						);
						break;
					}

					case "navigate": {
						console.log("[SandboxActions] Executing navigate action", { path: payload.path });
						navigate(payload.path);
						console.log("[SandboxActions] Navigation completed");
						break;
					}

					case "copy": {
						console.log("[SandboxActions] Executing copy action", { textLength: payload.text?.length });
						await navigator.clipboard.writeText(payload.text);
						console.log("[SandboxActions] Text copied to clipboard");
						toast.success("Copied to clipboard");
						break;
					}

					case "refresh": {
						console.log("[SandboxActions] Executing refresh action", { queryKey: payload.queryKey });
						// Trigger page refresh — query invalidation would need react-query context
						// For now, dispatch a custom event that consuming components can listen for
						window.dispatchEvent(
							new CustomEvent("sandbox-refresh", {
								detail: { queryKey: payload.queryKey },
							}),
						);
						console.log("[SandboxActions] Refresh event dispatched");
						toast.info("Refreshing data...");
						break;
					}

					default: {
						console.warn(
							"[SandboxActions] Unknown action:",
							(payload as Record<string, unknown>).action,
						);
					}
				}
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Action failed";
				console.error("[SandboxActions] Action error:", err);
				toast.error(message);
			}
		},
		[navigate, currentTenant, onChatFollowUp],
	);

	return handleOutput;
}
